/**
 * useLayoutProps Hook
 *
 * Extracts auth context and formats it for DashboardLayout props.
 * Provides centralized logic for passing user info, tenant info, and navigation handlers.
 */

import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import type { DashboardLayoutProps } from "../components/layout/DashboardLayout";
import type { AvailableRole, ActiveRoleDetails } from "../types/auth.types";

/**
 * Hook to extract auth context and provide DashboardLayout props
 */
export const useLayoutProps = (): Partial<DashboardLayoutProps> => {
  const {
    user,
    tenant,
    logout,
    activeRole,
    isRoleSessionActive,
    availableRoles,
    activateRole,
    deactivateRole,
    pendingRoleActivation2FA,
    verifyRoleActivation2FA,
    cancelRoleActivation2FA,
    expiredRoleInfo,
    dismissRoleSessionExpired,
    extendRoleSession,
  } = useAuth();
  const navigate = useNavigate();

  // Transform availableRoles from RoleAssignment[] to AvailableRole[]
  const transformedAvailableRoles: AvailableRole[] = availableRoles.map((r) => ({
    roleId: r.roleId,
    roleCode: r.roleCode,
    roleName: r.roleName,
  }));

  // Transform activeRole from ActiveRole (snake_case) to ActiveRoleDetails (camelCase)
  const transformedActiveRole: ActiveRoleDetails | null = activeRole
    ? {
        roleId: activeRole.role_id,
        roleCode: activeRole.role_code,
        roleName: activeRole.role_name,
        permissions: activeRole.permissions,
        expiresAt: activeRole.expires_at,
        remainingSeconds: 0, // Calculated in Header via useEffect
      }
    : null;

  return {
    tenantCode: tenant?.tenantCode,
    tenantName: tenant?.tenantName,
    tenantLogoUrl: tenant?.logoUrl,
    userName: user ? `${user.firstName} ${user.lastName}` : undefined,
    userEmail: user?.email,
    activeRole: transformedActiveRole,
    isRoleSessionActive,
    availableRoles: transformedAvailableRoles,
    pendingRoleActivation2FA,
    expiredRoleInfo,
    onActivateRole: activateRole,
    defaultRoleSessionDurationHours: typeof tenant?.settings?.defaultRoleSessionDurationHours === 'number'
      ? tenant.settings.defaultRoleSessionDurationHours
      : undefined,
    onDeactivateRole: async () => {
      await deactivateRole();
      navigate("/app/dashboard");
    },
    onVerifyRoleActivation2FA: verifyRoleActivation2FA,
    onCancelRoleActivation2FA: cancelRoleActivation2FA,
    onDismissRoleSessionExpired: async () => {
      await dismissRoleSessionExpired();
      navigate("/app/dashboard");
    },
    onExtendRoleSession: extendRoleSession,
    onLogout: () => {
      logout();
      navigate("/login");
    },
    onProfileClick: () => {
      navigate("/app/profile");
    },
    onSettingsClick: () => {
      navigate("/app/settings");
    },
    onBillingClick: () => {
      navigate("/app/billing");
    },
  };
};
